import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format, subDays,isToday, isAfter, isBefore, parseISO, formatDistanceToNow } from 'date-fns';
import "./styles/meily_woocommerce.css";
import LoadingIndicator from "../utils/loading/LoadingIndicator";
import { useLoading } from "../utils/loading/LoadingContext";
import DatePicker from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
const MeilyWoocommerce = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { loading, setLoading } = useLoading();

  // WooCommerce API credentials
  const consumerKey = process.env.REACT_APP_WOOCOMMERCE_CONSUMER_KEY;
  const consumerSecret = process.env.REACT_APP_WOOCOMMERCE_SECRET_KEY;
  const woocommerceAPIURL = process.env.REACT_APP_WOOCOMMERCE_API_URL;

  // Function to fetch orders
  const fetchOrders = async (page = 1) => {
    try {
      setLoading(true);

      let url = `${woocommerceAPIURL}?per_page=50&page=${page}`;

      const response = await axios.get(url, {
        auth: {
          username: consumerKey,
          password: consumerSecret,
        },
      });

      setOrders(response.data);
      setFilteredOrders(response.data); // Initially show all orders
      setTotalPages(parseInt(response.headers['x-wp-totalpages'], 10));
      setLoading(false);
    } catch (error) {
      setError(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Fetch orders for the last 7 days by default
    const today = new Date();
    const last7Days = subDays(today, 7);

    fetchOrders(page);
  }, [page]);

  

  const handleFilterSubmit = () => {
    if (startDate && endDate) {
      // Filter orders based on `date_created`
      const filtered = orders.filter((order) => {
        const orderDate = parseISO(order.date_created);
        return isAfter(orderDate, new Date(startDate)) && isBefore(orderDate, new Date(endDate));
      });
      setFilteredOrders(filtered);
    } else {
      setFilteredOrders(orders); // If no dates selected, show all orders
    }
  };

  const handlePrevPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  const handleRefresh = () => {
    setStartDate(null);
    setEndDate(null);
    fetchOrders(page); // Re-fetch orders when the refresh button is clicked
  };
  const handleClearDates = () => {
    setStartDate(null);
    setEndDate(null);
    setFilteredOrders(orders); // Reset filtered orders to show all orders
  };
  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  // Function to display the formatted order date or relative time if today
  const displayOrderDate = (dateCreated) => {
    const orderDate = parseISO(dateCreated);
  
    if (isToday(orderDate)) {
      // If the order was created today, show the relative time
      return formatDistanceToNow(orderDate, { addSuffix: true });
    } else {
      // Otherwise, show the formatted date as 'DD MMM YYYY'
      return format(orderDate, 'dd MMM yyyy');
    }
  };

  if (loading) {
    return <LoadingIndicator />; // Show loading indicator when loading
  }

  if (error) {
    return <div>Error fetching orders: {error.message}</div>;
  }
  const getStatusClass = (status) => {
    switch (status) {
      case 'processing':
        return 'status-badge status-processing';
      case 'cancelled':
        return 'status-badge status-cancelled';
      case 'completed':
        return 'status-badge status-completed';
      case 'failed':
        return 'status-badge status-failed';
      case 'payment pending':
        return 'status-badge status-payment-pending';
      default:
        return 'status-badge';
    }
  };
  
  return (
    <div className="woocommerce">
      <h1>Meily Online Shop Orders</h1>
      <div className='date-selection-woocommerce'>
       <div className='date-container'>
      <DatePicker
            selected={startDate}
            onChange={(date) => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            placeholderText="Start Date"
          />
          <DatePicker
            selected={endDate}
            onChange={(date) => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            placeholderText="End Date"
          />
        <button className="submit-button" onClick={handleFilterSubmit}>Submit</button>
        <button className="clear-button" onClick={handleClearDates}>
        Clear
      </button>
      <button className="refresh-button" onClick={handleRefresh}>Refresh</button>
      </div> 
     
      <div className="pagination">
        <button onClick={handlePrevPage} disabled={page === 1}>
          Previous
        </button>
        <span>Page {page} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={page === totalPages}>
          Next
        </button>
      </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>Order ID</th>
            <th>Customer Name</th>
            <th>Order Date</th> {/* Added Order Date Column */}
            <th>Status</th>
            <th>Total</th>
            <th>Payment Method</th>
            <th>Items</th>
            
          </tr>
        </thead>
        <tbody>
          {filteredOrders.map((order) => (
            <tr key={order.id}>
              <td>{order.id}</td>
              <td>{`${order.billing.first_name} ${order.billing.last_name}`}</td>
              <td>{displayOrderDate(order.date_created)}</td> {/* Added Order Date Display */}
              <td> <span className={getStatusClass(order.status)}>{order.status}</span></td>
              <td>{order.total}</td>
              <td>{order.payment_method}</td>
              <td>
                <ul>
                  {order.line_items.map((item) => (
                    <li key={item.id}>
                      {item.name} (x{item.quantity})
                    </li>
                  ))}
                </ul>
              </td>
             
            </tr>
          ))}
        </tbody>
      </table>

    
    </div>
  );
};

export default MeilyWoocommerce;
